import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Building2, Users, Briefcase, TrendingUp, DollarSign, BarChart3 } from "lucide-react";
import { fmtDate } from "@/lib/time";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

function Stat({ label, value, icon: Icon, sub }) {
    return (
        <div className="bg-muted border border-border p-6">
            <div className="flex items-start justify-between">
                <div>
                    <div className="overline mb-3">{label}</div>
                    <div className="font-serif text-4xl font-light">{value}</div>
                    {sub && <div className="text-xs text-muted-foreground mt-2">{sub}</div>}
                </div>
                <Icon className="w-5 h-5 text-primary" strokeWidth={1.5}/>
            </div>
        </div>
    );
}

export default function SuperAdminOverview() {
    const [stats, setStats] = useState(null);
    const [tenants, setTenants] = useState([]);
    useEffect(() => {
        api.get("/platform/analytics").then(r => setStats(r.data));
        api.get("/tenants").then(r => setTenants(r.data));
    }, []);

    return (
        <div className="space-y-12">
            <div>
                <div className="overline mb-3">Platform</div>
                <h1 className="font-serif text-3xl sm:text-5xl font-light">Inno Aim, command center.</h1>
                <p className="text-muted-foreground text-sm mt-3">Every tenant, every subscription, every rupee — at a glance.</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <Stat label="Tenants" value={stats?.total_tenants ?? "—"} icon={Building2} sub={`${stats?.active_tenants ?? 0} active · ${stats?.trial_tenants ?? 0} trial`}/>
                <Stat label="Users" value={stats?.total_users ?? "—"} icon={Users}/>
                <Stat label="Cases under management" value={stats?.total_cases ?? "—"} icon={Briefcase}/>
                <Stat label="Monthly recurring" value={`₹${(stats?.mrr ?? 0).toLocaleString()}`} icon={DollarSign}/>
                <Stat label="Annual recurring" value={`₹${(stats?.arr ?? 0).toLocaleString()}`} icon={TrendingUp}/>
                <Stat label="System status" value="Operational" icon={BarChart3} sub="All services online"/>
            </div>

            <div>
                <div className="overline mb-4">Recent tenants</div>
                <RecentTenantsTable tenants={tenants}/>
            </div>
        </div>
    );
}

function RecentTenantsTable({ tenants }) {
    const ctrl = useTableControls(tenants.slice(0, 10));
    return (
        <>
            <FilterChip ctrl={ctrl} className="mb-2"/>
            <div className="border border-border overflow-x-auto">
                <table className="min-w-[640px] w-full text-sm">
                    <thead className="bg-muted border-b border-border">
                        <tr className="text-left">
                            <SortableTh field="name" ctrl={ctrl}>Firm</SortableTh>
                            <SortableTh field="slug" ctrl={ctrl}>Slug</SortableTh>
                            <SortableTh field="status" ctrl={ctrl}>Status</SortableTh>
                            <SortableTh field="users_count" ctrl={ctrl}>Users</SortableTh>
                            <SortableTh field="cases_count" ctrl={ctrl}>Litigations</SortableTh>
                            <SortableTh field="created_at" ctrl={ctrl}>Joined</SortableTh>
                        </tr>
                    </thead>
                    <tbody>
                        {ctrl.rows.map(t => (
                            <tr key={t.id} className="border-b border-border hover:bg-muted/50">
                                <td className="px-4 py-3">{t.name}</td>
                                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{t.slug}</td>
                                <td className="px-4 py-3"><span className="text-xs uppercase tracking-[0.2em] text-primary">{t.status}</span></td>
                                <td className="px-4 py-3 text-muted-foreground">{t.users_count}</td>
                                <td className="px-4 py-3 text-muted-foreground">{t.cases_count}</td>
                                <td className="px-4 py-3 text-xs text-muted-foreground">{fmtDate(t.created_at)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}